import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { PSKLoader } from "~/util/3d/loaders/PSKLoader";
import { PSALoader } from "~/util/3d/loaders/PSALoader";
import { TGALoader } from "~/util/3d/loaders/TGALoader";
import { Background } from "./Background";
import { useRealm } from "~/util/Lang";

// Определяем интерфейсы для анимации и конфигурации
interface AnimationData {
  name: string;
  file: string;
  conf: string;
}

function normalizeModel(mesh: THREE.Mesh, targetHeight: number = 6) {
  // Создаем ограничивающий объем для модели
  const box = new THREE.Box3().setFromObject(mesh);

  // Вычисляем размеры модели
  const size = new THREE.Vector3();
  box.getSize(size);

  // Вычисляем текущую высоту модели
  const currentHeight = size.y;

  // Вычисляем коэффициент масштабирования для высоты
  const scaleFactor = targetHeight / currentHeight;

  // Масштабируем модель
  mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);

  // Пересчитываем центр модели после масштабирования
  box.setFromObject(mesh);
  const center = new THREE.Vector3();
  box.getCenter(center);

  // Сдвигаем модель так, чтобы центр оказался в точке (0, 0, 0)
  mesh.position.sub(center);
}

function Model(props: {
  npc: {
    meshPath: string;
    mesh: string;
    texturePath?: string;
    materials?: {
      name?: string;
      diffuse: string;
      specular?: string;
      opacity?: string;
      params?: {
        outputBlending: number;
        twoSided: boolean;
      };
    }[];
    animationPath?: string;
    animation?: string;
  };
}) {
  const realm = useRealm();
  const { scene } = useThree();
  const [mixer, setMixer] = useState<THREE.AnimationMixer | null>(null);
  const meshRef = useRef<THREE.SkinnedMesh | null>(null); // Ссылка на загруженный меш

  const PATH_TEXTURE = `s/${realm.game}/${props.npc.texturePath}/Texture`;
  const PATH_MAT = `s/${realm.game}/${props.npc.texturePath}/Shader`;
  const PATH_MESH = `s/${realm.game}/${props.npc.meshPath}`;
  const PATH_ANIMATION = `s/${realm.game}/${props.npc.animationPath}`;

  const ANIMATIONS: AnimationData[] = [
    {
      name: `${props.npc.animation}`,
      file: `/${PATH_ANIMATION}/${props.npc.animation}.psa`,
      conf: `/${PATH_ANIMATION}/${props.npc.animation}.config`,
    },
  ];

  useEffect(() => {
    const loader = new PSKLoader();
    const loaderAnimations = new PSALoader();
    const textureLoader = new TGALoader();

    // Удаляем старую модель из сцены перед загрузкой новой
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null; // Обнуляем ссылку на старую модель
    }

    async function loadModel() {
      loader.load(
        {
          url: `/${PATH_MESH}/${props.npc.mesh}.psk`,
          PathMaterials: `/${PATH_MAT}/`,
        },
        (
          geometry: THREE.BufferGeometry,
          materials: THREE.Material[],
          options: any,
          skeleton: THREE.Skeleton,
        ) => {
          const mat: THREE.MeshStandardMaterial[] = [];

          const textures = genTextures(
            props.npc.materials ?? [],
            materials as any,
          );

          for (const t of textures) {
            const texture = textureLoader.load(
              `/${PATH_TEXTURE}/${t.diffuse}.tga`,
            );
            const specularTexture = t.specular
              ? textureLoader.load(`/${PATH_TEXTURE}/${t.specular}.tga`)
              : null;
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;

            const material = new THREE.MeshStandardMaterial({
              map: texture,
              metalnessMap: specularTexture,
              transparent: t.diffuse === t.specular ? false : true,
              opacity: 1,
              alphaTest: 0,

              side: THREE.DoubleSide, // Двусторонний рендеринг
              // ...(props.npc.params.outputBlending > 0 && {
              //   blending: THREE.AdditiveBlending,
              // }), // Режим "осветления"
            });
            mat.push(material);
          }

          const mesh = new THREE.SkinnedMesh(geometry, mat);
          // mesh.scale.set(0.1, 0.1, 0.1);
          mesh.add(skeleton.bones[0]); // Присоединяем корневую кость
          mesh.bind(skeleton);

          meshRef.current = mesh; // Сохраняем ссылку на загруженный меш

          if (meshRef.current) {
            normalizeModel(meshRef.current);
          }

          scene.add(mesh);

          const newMixer = new THREE.AnimationMixer(mesh);
          setMixer(newMixer);

          loaderAnimations.loadList(ANIMATIONS, (animations: any) => {
            const animationMap = new Map<string, THREE.AnimationClip>();
            for (const a of animations) {
              for (const ac of a.animation as THREE.AnimationClip[]) {
                if (
                  ["Wait", "Wait_1HS", "wait_Hand"].some((subStr) =>
                    ac.name.startsWith(subStr),
                  )
                ) {
                  animationMap.set("wait", ac);
                }
              }
            }
            const aWait = animationMap.get("wait");
            if (aWait) {
              const action = newMixer.clipAction(aWait);
              action.play();
            }
          });
        },
        undefined,
        (error: Error) => {
          console.error("Error loading model:", error);
        },
      );
    }

    loadModel();

    // Очищаем миксер при размонтировании компонента
    return () => {
      if (mixer) {
        mixer.stopAllAction(); // Останавливаем все действия миксера
        setMixer(null);
      }
    };
  }, []); // Загружаем модель при изменении NPC

  useFrame((state, delta) => {
    if (mixer) {
      mixer.update(delta); // Обновляем миксер для воспроизведения анимации
    }
  });

  return null;
}

export default function NpcModel(props: {
  npc: {
    meshPath: string;
    mesh: string;
    texturePath?: string;
    materials?: {
      name?: string;
      diffuse: string;
      specular?: string;
      opacity?: string;
    }[];
    animationPath?: string;
    animation?: string;
    params: {
      outputBlending: number;
    };
  };
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        setCanvasSize({
          width: canvasRef.current.clientWidth,
          height: canvasRef.current.clientHeight,
        });
      }
    };

    const resizeObserver = new ResizeObserver(updateCanvasSize);

    if (canvasRef.current) {
      resizeObserver.observe(canvasRef.current);
      updateCanvasSize(); // Изначальное обновление
    }

    return () => {
      if (canvasRef.current) {
        resizeObserver.unobserve(canvasRef.current);
      }
    };
  }, []);

  return (
    <div style={{ width: "100%", height: "500px" }}>
      <Canvas
        style={{ width: "100%", height: "500px" }}
        ref={canvasRef}
        camera={{ position: [0, 0, 10] }}
      >
        <Background
          clientWidth={canvasSize.width}
          clientHeight={canvasSize.height}
        />

        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <Model npc={props.npc} />
        <OrbitControls />
      </Canvas>
    </div>
  );
}

function genTextures(
  textures: {
    name?: string;
    diffuse: string;
    specular?: string;
    opacity?: string;
  }[],
  materials: { Name: string }[],
) {
  const texturMap = new Map<
    string,
    {
      name?: string;
      diffuse: string;
      specular?: string;
      opacity?: string;
    }
  >();
  const materialMap = new Map<string, string>();

  for (const t of textures) {
    const e = t.diffuse.match(/_t.*$/)?.[0];
    texturMap.set(e!, t);
    t.name && materialMap.set(t.name, t.name);
  }

  for (const m of materials) {
    const e = m.Name.match(/_t.*$/)?.[0];
    if (
      !m.Name.includes("material_") &&
      !materialMap.has(m.Name) &&
      !texturMap.has(e!)
    ) {
      texturMap.set(e!, { diffuse: m.Name });
    }
  }

  return Array.from(texturMap.values());
}
